import React, { useState, useEffect } from "react";
import { C, fmt, text } from "../theme";

import { Card, Btn, Notice, StatusPill as Badge } from "../components/ui";

export default function BudgetPage({ onNavigate }) {
  const [budgets, setBudgets] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [uploadData, setUploadData] = useState("");
  const [uploading, setUploading] = useState(false);
  const [notice, setNotice] = useState(null); // { kind: "success" | "error", text }

  const load = () => {
    setLoading(true);
    Promise.all([
      fetch("/api/budgets").then(r => r.json()),
      fetch("/api/budgets/summary").then(r => r.json()),
    ]).then(([b, s]) => {
      setBudgets(b);
      setSummary(s);
    }).catch(console.error).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const handleUpload = async () => {
    try {
      const lines = uploadData.trim().split("\n").filter(l => l.trim());
      const budgetLines = lines.map(line => {
        const parts = line.split(",").map(p => p.trim());
        if (parts.length < 4) throw new Error("Each line needs: BU, Ref, Category, Annual. Got: " + line);
        return {
          bu: parts[0],
          ref: parts[1],
          category: parts[2],
          annual: parseFloat(parts[3]),
          fy: "FY2026",
        };
      });

      setUploading(true);
      const res = await fetch("/api/budgets/upload", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ budgets: budgetLines, fileName: "Manual CSV Upload" }),
      });
      const data = await res.json();
      // On failure the pasted CSV stays in the textarea for correction.
      if (!res.ok) { setNotice({ kind: "error", text: "Error: " + data.error }); return; }
      setNotice({ kind: "success", text: data.message });
      setShowUpload(false);
      setUploadData("");
      load();
    } catch (err) {
      setNotice({ kind: "error", text: "Error: " + err.message });
    } finally {
      setUploading(false);
    }
  };

  if (loading) return <div style={{ padding: 40, textAlign: "center", color: C.gray500 }}>Loading...</div>;

  const totalBudget = budgets.reduce((s, b) => s + b.annual, 0);
  const totalCommitted = budgets.reduce((s, b) => s + b.committedPOs, 0);
  const totalUtilized = budgets.reduce((s, b) => s + b.utilizedPOs, 0);
  const totalApprovedAORs = budgets.reduce((s, b) => s + b.approvedAORs, 0);
  const totalAvailable = budgets.reduce((s, b) => s + b.available, 0);
  const overBudgetCount = budgets.filter(b => b.status === "OVER_BUDGET").length;
  const virementCount = summary?.executedVirementCount || 0;

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div>
          <h1 style={{ fontSize: text.h1, fontWeight: 700, color: C.gray1100, margin: 0 }}>Budget Management</h1>
          <p style={{ fontSize: text.md, color: C.gray500, margin: "2px 0 0" }}>Upload budget file and track utilisation</p>
        </div>
        <Btn onClick={() => setShowUpload(!showUpload)}>📤 Upload Budget</Btn>
      </div>

      <Notice notice={notice} onDismiss={() => setNotice(null)} />

      {/* Upload panel */}
      {showUpload && (
        <Card style={{ marginBottom: 16, border: `2px solid ${C.primary}30` }}>
          <div style={{ fontSize: text.lg, fontWeight: 700, marginBottom: 4 }}>Upload Budget Data</div>
          <div style={{ fontSize: text.sm, color: C.gray500, marginBottom: 12 }}>
            Paste CSV data with format: <strong>BU, Budget Ref, Category, Annual Amount</strong> (one line per budget line). Existing refs will be updated; new refs will be created.
          </div>
          <textarea aria-label="Budget CSV data" value={uploadData} onChange={e => setUploadData(e.target.value)} placeholder={`NIC, NIC-OPEX-2026, Operating Expenses, 500000\nNIC, NIC-CAPEX-2026, Capital Expenditure, 200000\nSSIR, SSIR-OPEX-2026, Operating Expenses, 350000`} style={{ width: "100%", minHeight: 120, padding: "10px 12px", border: `1px solid ${C.gray300}`, borderRadius: 6, fontSize: text.md, fontFamily: "monospace", resize: "vertical", boxSizing: "border-box", marginBottom: 12 }} />
          <div style={{ display: "flex", gap: 8 }}>
            <Btn onClick={handleUpload} disabled={uploading || !uploadData.trim()}>{uploading ? "Uploading..." : "Process Upload"}</Btn>
            <Btn variant="outline" onClick={() => setShowUpload(false)}>Cancel</Btn>
          </div>
        </Card>
      )}

      {/* Current file info */}
      {summary?.fileName && (
        <Card style={{ marginBottom: 16, background: C.successBg, border: `1px solid ${C.successLight}` }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ width: 40, height: 40, borderRadius: 8, background: C.success, display: "flex", alignItems: "center", justifyContent: "center", color: C.white, fontSize: text.h2 }}>✓</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: text.md, fontWeight: 700 }}>{summary.fileName}</div>
              <div style={{ fontSize: text.sm, color: C.gray600 }}>
                {/* UX-50: a role-scoped subset must not read as the whole file. */}
                {summary.scopeLimited
                  ? `${summary.budgetLines} of ${summary.totalLines} budget lines (scope-limited) • Total of your ${summary.budgetLines} lines: ${fmt(summary.totalBudget)}`
                  : `${summary.budgetLines} budget lines • Total: ${fmt(summary.totalBudget)}`}
                {summary.uploadedAt && ` • Uploaded: ${new Date(summary.uploadedAt).toLocaleDateString()}`}
              </div>
              {/* UX-48: executed virements silently move annuals under this banner. */}
              {virementCount > 0 && (
                <div style={{ fontSize: text.sm, color: C.gray700, marginTop: 4 }}>
                  🔀 Annual figures include {virementCount} executed virement{virementCount > 1 ? "s" : ""} since upload —{" "}
                  <button onClick={() => onNavigate && onNavigate("virement")} style={{ background: "none", border: "none", padding: 0, cursor: "pointer", fontFamily: "inherit", fontSize: text.sm, fontWeight: 600, color: C.primary, textDecoration: "underline" }}>
                    view virements
                  </button>
                </div>
              )}
            </div>
          </div>
        </Card>
      )}

      {/* Summary cards */}
      <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 20 }}>
        {[
          { label: "Total Budget", value: fmt(totalBudget), color: C.primary },
          { label: "Committed (POs)", value: fmt(totalCommitted), color: C.warning },
          { label: "Utilized (Fulfilled)", value: fmt(totalUtilized), color: C.info },
          { label: "Approved AORs (not yet PO)", value: fmt(totalApprovedAORs), color: C.purple },
          { label: "Available", value: fmt(totalAvailable), color: totalAvailable < 0 ? C.danger : C.success },
          { label: "Over Budget Lines", value: overBudgetCount, color: overBudgetCount > 0 ? C.danger : C.success },
        ].map((s, i) => (
          <div key={i} style={{ flex: 1, minWidth: 140, background: C.white, border: `1px solid ${C.gray200}`, borderRadius: 10, padding: "16px 20px" }}>
            <div style={{ fontSize: text.sm, color: C.gray500, marginBottom: 4 }}>{s.label}</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: C.gray1100 }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Utilisation chart */}
      <Card style={{ marginBottom: 20 }}>
        <div style={{ fontSize: text.lg, fontWeight: 700, marginBottom: 14 }}>Budget Utilisation by Business Unit</div>
        {budgets.map(b => (
          <div key={b.id} style={{ marginBottom: 14 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
              <div style={{ fontSize: text.md }}>
                <strong>{b.bu}</strong> — {b.ref} <span style={{ color: C.gray500 }}>({b.category})</span>
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <span style={{ fontSize: text.sm, color: C.gray600 }}>{fmt(b.committedPOs + b.utilizedPOs + b.approvedAORs)} / {fmt(b.annual)}</span>
                <Badge status={b.status} />
              </div>
            </div>
            <div style={{ background: C.gray100, borderRadius: 6, height: 16, overflow: "hidden", display: "flex" }}>
              <div style={{ width: `${(b.utilizedPOs / b.annual) * 100}%`, background: C.info, height: "100%", transition: "width 0.3s" }} />
              <div style={{ width: `${(b.committedPOs / b.annual) * 100}%`, background: C.warning, height: "100%", transition: "width 0.3s" }} />
              <div style={{ width: `${(b.approvedAORs / b.annual) * 100}%`, background: C.purple, height: "100%", transition: "width 0.3s" }} />
            </div>
          </div>
        ))}
        <div style={{ display: "flex", gap: 16, marginTop: 12, fontSize: text.xs, color: C.gray600 }}>
          <span>🔵 Utilized</span>
          <span>🟡 Committed (POs)</span>
          <span>🟣 Approved AORs (not yet PO)</span>
          <span>⚪ Available</span>
        </div>
      </Card>

      {/* Budget table */}
      <Card style={{ padding: 0, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: text.md }}>
          {/* UX-22: "Pending PRs" was the pre-rename term — the module is AOR now. */}
          <thead><tr>{["BU", "Budget Ref", "Category", "Annual", "Committed", "Utilized", "Approved AORs", "Pending AORs", "Available", "Virements", "Usage", "Status"].map((h, i) => (
            <th scope="col" key={i} style={{ padding: "10px 12px", textAlign: "left", borderBottom: `2px solid ${C.gray200}`, color: C.gray600, fontWeight: 600, fontSize: text.xs, textTransform: "uppercase" }}>{h}</th>
          ))}</tr></thead>
          <tbody>
            {budgets.map(b => (
              <tr key={b.id} style={{ borderBottom: `1px solid ${C.gray100}` }}>
                <td style={{ padding: "10px 12px", fontWeight: 700 }}>{b.bu}</td>
                <td style={{ padding: "10px 12px", color: C.primary, fontWeight: 600 }}>{b.ref}</td>
                <td style={{ padding: "10px 12px" }}>{b.category}</td>
                <td style={{ padding: "10px 12px" }}>{fmt(b.annual)}</td>
                <td style={{ padding: "10px 12px", color: C.warning, fontWeight: 600 }}>{fmt(b.committedPOs)}</td>
                <td style={{ padding: "10px 12px", color: C.info, fontWeight: 600 }}>{fmt(b.utilizedPOs)}</td>
                <td style={{ padding: "10px 12px", color: C.purple, fontWeight: 600 }}>{fmt(b.approvedAORs)}</td>
                <td style={{ padding: "10px 12px", color: C.gray600 }}>{fmt(b.pendingAORs)}</td>
                <td style={{ padding: "10px 12px", fontWeight: 700, color: b.available < 0 ? C.danger : C.success }}>{fmt(b.available)}</td>
                <td style={{ padding: "10px 12px" }}>
                  {b.virements?.count > 0 ? (
                    <button onClick={() => onNavigate && onNavigate("virement")}
                      title={`${b.virements.count} executed virement(s) adjusted this line's annual by ${b.virements.net >= 0 ? "+" : ""}${fmt(b.virements.net)}`}
                      style={{ background: "none", border: "none", padding: 0, cursor: "pointer", fontFamily: "inherit", fontSize: text.sm, fontWeight: 600, color: C.purple, textDecoration: "underline" }}>
                      🔀 {b.virements.count} ({b.virements.net >= 0 ? "+" : ""}{fmt(b.virements.net)})
                    </button>
                  ) : <span style={{ color: C.gray400 }}>—</span>}
                </td>
                <td style={{ padding: "10px 12px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <div style={{ width: 50, background: C.gray100, borderRadius: 3, height: 8, overflow: "hidden", display: "flex" }}>
                      <div style={{ width: `${(b.utilizedPOs / b.annual) * 100}%`, background: C.info, height: "100%" }} />
                      <div style={{ width: `${(b.committedPOs / b.annual) * 100}%`, background: C.warning, height: "100%" }} />
                      <div style={{ width: `${(b.approvedAORs / b.annual) * 100}%`, background: C.purple, height: "100%" }} />
                    </div>
                    <span style={{ fontSize: text.xs, color: C.gray600 }}>{b.pct.toFixed(0)}%</span>
                  </div>
                </td>
                <td style={{ padding: "10px 12px" }}><Badge status={b.status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}